import React from 'react';
import { formatMoney, onAccent, type StorefrontConfig } from '../types';

/**
 * Boutique — fashion & apparel.
 *
 * Convention research (Dribbble, fashion/apparel e-commerce): the shops in
 * this vertical are near-monochrome, lean on one oversized display word,
 * run full-bleed imagery, and strip the UI back to almost nothing. Price is
 * present but quiet; the photograph is doing the selling, so chrome that
 * competes with it is chrome that costs a sale.
 *
 * What that means here: a full-height hero with the shop name set huge, a
 * two-column grid at desktop with tall 4:5 tiles, uppercase micro-labels,
 * and hairline rules instead of cards or shadows.
 */
export default function Boutique({ config }: { config: StorefrontConfig }) {
  const ink = '#16191D';

  return (
    <div style={{ background: '#FFFFFF', color: ink, fontFamily: 'var(--font-family)' }}>
      {config.announcement ? (
        <div
          style={{
            background: config.accent,
            color: onAccent(config.accent),
            textAlign: 'center',
            padding: '10px 16px',
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {config.announcement}
        </div>
      ) : null}

      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px clamp(16px, 5vw, 64px)',
          borderBottom: `1px solid ${ink}14`,
          position: 'sticky',
          top: 0,
          background: '#FFFFFFEE',
          backdropFilter: 'blur(8px)',
          zIndex: 5,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          {config.name}
        </span>
        <nav style={{ display: 'flex', gap: 'clamp(14px, 3vw, 32px)', flexWrap: 'wrap' }}>
          {config.collections.map((c) => (
            <span key={c} style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.7 }}>
              {c}
            </span>
          ))}
        </nav>
      </header>

      {/* Hero. The shop name is the graphic — set to the viewport rather than
          a fixed size so it stays edge-to-edge on any screen. */}
      <section
        style={{
          position: 'relative',
          minHeight: 'min(74vh, 620px)',
          display: 'flex',
          alignItems: 'flex-end',
          padding: 'clamp(24px, 5vw, 64px)',
          background: `linear-gradient(120deg, ${config.products[0]?.swatch[0] ?? '#DDD'}, ${
            config.products[2]?.swatch[1] ?? '#888'
          })`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.46), rgba(0,0,0,0.05) 55%)',
          }}
        />
        <div style={{ position: 'relative', color: '#FFFFFF', maxWidth: 720 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', opacity: 0.85 }}>
            {config.category}
          </span>
          <h1
            style={{
              fontSize: 'clamp(44px, 11vw, 132px)',
              lineHeight: 0.92,
              fontWeight: 700,
              letterSpacing: '-0.03em',
              margin: '12px 0 16px',
              textTransform: 'uppercase',
            }}
          >
            {config.name}
          </h1>
          <p style={{ fontSize: 'clamp(14px, 1.6vw, 18px)', maxWidth: 460, lineHeight: 1.5, opacity: 0.92 }}>
            {config.bio}
          </p>
        </div>
      </section>

      <section style={{ padding: 'clamp(40px, 7vw, 96px) clamp(16px, 5vw, 64px)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 'clamp(20px, 3vw, 40px)',
          }}
        >
          <h2 style={{ fontSize: 'clamp(20px, 3vw, 30px)', fontWeight: 700, letterSpacing: '-0.02em' }}>
            The collection
          </h2>
          <span style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.55 }}>
            {config.products.length} pieces
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
            gap: 'clamp(16px, 2.5vw, 40px)',
          }}
        >
          {config.products.map((p) => (
            <article key={p.id}>
              <div
                style={{
                  position: 'relative',
                  aspectRatio: '4 / 5',
                  background: `linear-gradient(150deg, ${p.swatch[0]}, ${p.swatch[1]})`,
                  marginBottom: 14,
                }}
              >
                {p.badge ? (
                  <span
                    style={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      background: '#FFFFFF',
                      color: ink,
                      fontSize: 10,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      padding: '5px 9px',
                    }}
                  >
                    {p.badge}
                  </span>
                ) : null}
                {p.soldOut ? (
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(255,255,255,0.7)',
                      fontSize: 11,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}
                  >
                    Sold out
                  </span>
                ) : null}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</span>
                {config.showPrices ? (
                  <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                    {p.compareAt ? (
                      <span style={{ opacity: 0.4, textDecoration: 'line-through', marginRight: 6 }}>
                        {formatMoney(p.compareAt, config.currency)}
                      </span>
                    ) : null}
                    {formatMoney(p.price, config.currency)}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.6 }}>
                    Enquire
                  </span>
                )}
              </div>
              <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.45 }}>
                {p.category}
              </span>
            </article>
          ))}
        </div>
      </section>

      <footer
        style={{
          borderTop: `1px solid ${ink}14`,
          padding: 'clamp(28px, 5vw, 56px) clamp(16px, 5vw, 64px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          justifyContent: 'space-between',
          fontSize: 13,
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <strong style={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 12 }}>{config.name}</strong>
          <span style={{ opacity: 0.6 }}>{config.location}</span>
          <span style={{ opacity: 0.6 }}>{config.hours}</span>
        </div>
        <span style={{ opacity: 0.45 }}>{config.domain}</span>
      </footer>
    </div>
  );
}
